import { Navigate } from 'react-router-dom';
import { Alert, Button, DetailPage, Page, Spinner } from '@nexvia-logistics/ui';
import { ApprovalStatusBanner } from './ApprovalStatusBanner';
import { BankAccountForm } from './BankAccountForm';
import { DepotRatingCard } from './DepotRatingCard';
import { DocumentsCard } from './DocumentsCard';
import { FiscalDataForm } from './FiscalDataForm';
import { QuoteContactForm } from './QuoteContactForm';
import { useEmpresaData } from './useEmpresaData';

/** `/mi-empresa` (Milestone 2): fiscal data + approval status + the three fixed documents,
 *  against the real `GET/PATCH /storage/me` and `/storage/documents/*` endpoints.
 *  NEX-1164 (2026-10-15): the page chrome is the real `DetailPage` — title, description, the
 *  approval banner in its `notice` slot and the three cards in its column (spec S6, D-6.1); the
 *  loading and error states are `Spinner` and `Page`+`Alert`.
 *  NEX-1164 (2026-10-16): the banner shows the five account states with the reject/suspend reason
 *  and the upload CTA; `QuoteContactForm` (editable in every state) sits under the fiscal data; a
 *  login with no depósito account (`no-profile`) is sent to `/registro`.
 *  NEX-1164 design pass (2026-10-18, spec §3.6): the mock's narrow centred column (a wrapper div —
 *  `Page` already emits its own `max-w-[1440px]`) and its order: banner, Datos fiscales,
 *  Documentación, Avisos (`QuoteContactForm`), Calificación de tus clientes. The "Baja del depósito"
 *  notice is gone: it was one of the mock's dashed annotation boxes, a note for the team.
 *  NEX-1169 (2026-10-20): "Cuenta bancaria" (`BankAccountForm`, no mock) between Documentación and
 *  Avisos — with no account saved it reads "Cargá tu cuenta bancaria para que podamos pagarte." */
export default function EmpresaPage() {
  const { profile, documents, status, reload, setProfile } = useEmpresaData();

  if (status === 'loading') {
    return (
      <div className="grid place-items-center p-10">
        <Spinner size={32} label="Cargando los datos de tu empresa…" />
      </div>
    );
  }

  // NEX-1164 — a login with no depósito account registers first (`/registro`), it has nothing to show here.
  if (status === 'no-profile') {
    return <Navigate to="/registro" replace />;
  }

  if (status === 'error' || !profile) {
    return (
      <Page className="py-6">
        <Alert tone="critical">No pudimos cargar los datos de tu empresa.</Alert>
        <div>
          <Button type="button" variant="primary" size="xs" showArrow={false} onClick={() => void reload()}>
            Reintentar
          </Button>
        </div>
      </Page>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[44rem]">
      <DetailPage
        title="Mi empresa"
        // Real design copy (Claude Design mockup), matched closely rather than paraphrased.
        description="El alta la aprueba Nexvia, igual que la de los transportistas. Estar en la plataforma no tiene costo."
        notice={
          <ApprovalStatusBanner
            status={profile.approvalStatus}
            nextStep={profile.nextStep}
            reviewedAt={profile.reviewedAt}
            rejectionReason={profile.rejectionReason}
            submittedAt={profile.submittedAt}
          />
        }
      >
        <FiscalDataForm profile={profile} onSaved={setProfile} />
        <DocumentsCard documents={documents} onChanged={() => void reload()} />
        {/* NEX-1169 — where Nexvia pays the depósito; loads on its own, with its own notice when empty. */}
        <BankAccountForm profile={profile} />
        <QuoteContactForm profile={profile} onSaved={setProfile} />
        {/* Audit S5 — the clients' rating of the depósito (contract C1); nothing without `rating`. */}
        <DepotRatingCard rating={profile.rating} />
      </DetailPage>
    </div>
  );
}
